import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { I18n } from 'react-redux-i18n';
import { media } from '../theme/tokens';

import { useDistributionGlobalDataFilters } from 'apiTyped/hooks/global_data_view.query';
import { useV2DistributionGeoFilters } from 'apiTyped/hooks/v2.distribution.query';
import { useV2Filters } from '../context/V2FiltersContext';
import {
  SelectDropdown,
  MultiSelectDropdown,
  SelectOption
} from './SelectDropdown';
import { useMemo, useCallback } from 'react';
import {
  DEFAULT_FILTERS,
  PosFilterState,
  PRICE_RANGE_BOUNDS,
  countActiveFilters,
  OFF_TRADE_SPIRIT_FORMATS,
  OFF_TRADE_SPIRIT_POS_TYPES,
  filtersForChannel,
  getMarketSelectionContext,
  getMarketsForContext,
  setMarketsForContext
} from './posFilterTypes';

// Discrete "Median price per POS" buckets shown as quick-pick buttons,
// replacing the previous free-range slider. Bounds match PRICE_RANGE_BOUNDS
// (the untouched/"All" state used across Global View / Points of Sale).
const SPIRITS_EXCLUDED_POS_TYPES = ['wine retailer', 'wine shop'];

// Spirits store awards; values mirror store_restaurant.worlds_best / top_bar /
// spirited_award.
const WORLDS_BEST_OPTIONS: SelectOption[] = [
  { value: 'Top50', label: 'Top 50' },
  { value: 'Top100', label: 'Top 100' },
  { value: '50discovery', label: '50 Best Discovery' }
];
const TOP_BAR_OPTIONS: SelectOption[] = [
  { value: 'Top500', label: 'Top 500 Bars' }
];
const SPIRITED_AWARD_OPTIONS: SelectOption[] = [
  { value: 'Winner', label: 'Spirited Awards Winner' }
];

type AwardKind = 'ws' | 'michelin' | 'wb' | 'bar' | 'sa';

const AWARD_GROUPS = {
  ws: 'Wine Spectator',
  michelin: 'Michelin',
  wb: "World's 50 Best",
  bar: 'Top Bars',
  sa: 'Spirited Awards'
} as const;

const PRICE_BRACKETS: {
  key: string;
  min: number;
  max: number;
}[] = [
  { key: 'b1', min: 0, max: 2000 },
  { key: 'b2', min: 2000, max: 4000 },
  { key: 'b3', min: 4000, max: 6000 },
  { key: 'b4', min: 6000, max: 8000 },
  { key: 'b5', min: 8000, max: PRICE_RANGE_BOUNDS[1] }
];

interface PosFilterModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  posCount: number;
  filters: PosFilterState;
  onChange: (updater: (prev: PosFilterState) => PosFilterState) => void;
  onReset: () => void;
  currency?: string;
}

/**
 * Full-width modal overlay for POS filters.
 * Reusable across CRM page and Points of Sale tab.
 * Matches the reference design: ws-filter-panel--modal.
 */
export const PosFilterModal: React.FC<PosFilterModalProps> = ({
  open,
  onClose,
  title = 'Filter your accounts',
  posCount,
  filters: localFilters,
  onChange,
  onReset,
  currency = 'USD'
}) => {
  const { filters: contextFilters } = useV2Filters();
  const priceFormatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    maximumFractionDigits: 0
  });
  const marketSelectionContext = getMarketSelectionContext(
    contextFilters.channel,
    contextFilters.contentMode
  );
  const marketScope = {
    network: contextFilters.channel === 'on_trade' ? 'on' : 'off',
    premium_store:
      contextFilters.channel === 'on_trade' &&
      contextFilters.contentMode === 'premium'
        ? true
        : undefined,
    survey_year:
      localFilters.surveyYear === 'all' ? 'current' : localFilters.surveyYear
  } as const;
  const { filters: apiFilters } = useDistributionGlobalDataFilters(marketScope);
  const isOnTrade = contextFilters.channel === 'on_trade';
  const isSpirits = contextFilters.productType === '2';
  const { continents, countries } = useV2DistributionGeoFilters({
    network: marketScope.network,
    premium_store: marketScope.premium_store
  });

  const [draft, setDraft] = useState<PosFilterState>(localFilters);

  useEffect(() => {
    if (open) {
      setDraft({
        ...filtersForChannel(localFilters, contextFilters.channel, isSpirits),
        markets: getMarketsForContext(localFilters, marketSelectionContext)
      });
    }
  }, [
    localFilters,
    open,
    contextFilters.channel,
    isSpirits,
    marketSelectionContext
  ]);

  const handleApply = useCallback(() => {
    onChange(() =>
      setMarketsForContext(
        filtersForChannel(draft, contextFilters.channel, isSpirits),
        marketSelectionContext,
        draft.markets
      )
    );
    onClose();
  }, [
    draft,
    onChange,
    onClose,
    contextFilters.channel,
    marketSelectionContext
  ]);

  const handleCancel = useCallback(() => {
    setDraft(localFilters);
    onClose();
  }, [localFilters, onClose]);

  const handleReset = useCallback(() => {
    setDraft((previous) => ({
      ...DEFAULT_FILTERS,
      surveyYear: previous.surveyYear,
      surveyYearSelections: previous.surveyYearSelections
    }));
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancel();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleCancel, open]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // --- Build options from API ---
  const marketsOptions = useMemo(
    () =>
      (apiFilters.markets || []).map((market: any) => ({
        value: market.id,
        label: market.name
      })),
    [apiFilters.markets]
  );

  const colorsOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: '1', label: 'Red' },
      { value: '4', label: 'White' },
      { value: '2', label: 'Rosé' },
      { value: '5', label: 'Sparkling' },
      { value: '3', label: 'Sweet' },
      { value: '6', label: 'Fortified' }
    ],
    []
  );

  const grapeOptions = useMemo(
    () =>
      (apiFilters.wine_grape_varieties || []).map((g: any) => ({
        value: g.id,
        label: g.name
      })),
    [apiFilters.wine_grape_varieties]
  );

  const wineCountryOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...(apiFilters.wine_countries || []).map((c: any) => ({
        value: c.id,
        label: c.name
      }))
    ],
    [apiFilters.wine_countries]
  );

  const wineRegionOptions = useMemo(() => {
    if (draft.wineCountry === 'all') return [{ value: 'all', label: 'All' }];
    const regions = (apiFilters.wine_regions || [])
      .filter(
        (r: any) => String(r.wine_country_id) === String(draft.wineCountry)
      )
      .map((r: any) => ({ value: r.id, label: r.name }))
      .sort((a: SelectOption, b: SelectOption) =>
        String(a.label).localeCompare(String(b.label))
      );
    return [{ value: 'all', label: 'All' }, ...regions];
  }, [apiFilters.wine_regions, draft.wineCountry]);

  const formatOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...(contextFilters.productType === '2'
        ? apiFilters.spirit_format_groups || []
        : apiFilters.wine_format_groups || []
      ).map((f: any) => ({
        value: f.id,
        label: f.name
      }))
    ],
    [
      apiFilters.spirit_format_groups,
      apiFilters.wine_format_groups,
      contextFilters.productType
    ]
  );

  const spiritFormatOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...(apiFilters.spirit_format_groups || []).map((f: any) => ({
        value: f.id,
        label: f.name
      }))
    ],
    [apiFilters.spirit_format_groups]
  );

  // "Category of spirit" — real wine_sub_type rows already scoped to
  // type_id=2 server-side (see get_wine_sub_types), so no client-side
  // filtering is needed here, unlike the prototype's deterministic mock.
  const spiritCategoryOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...(apiFilters.wine_sub_types || []).map((s: any) => ({
        value: s.id,
        label: s.name
      }))
    ],
    [apiFilters.wine_sub_types]
  );

  // "Country of origin" — real spirit_countries rows (country of the
  // spirit's producing wine, same wine_country_id the backend expects).
  const spiritCountryOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: SelectOption[] = [{ value: 'all', label: 'All' }];
    (apiFilters.spirit_countries || []).forEach((c: any) => {
      const key = String(c.id);
      if (seen.has(key)) return;
      seen.add(key);
      options.push({ value: c.id, label: c.name });
    });
    return options;
  }, [apiFilters.spirit_countries]);

  const appellationOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...(apiFilters.wine_appelations || []).map((a: any) => ({
        value: a.id,
        label: a.name
      }))
    ],
    [apiFilters.wine_appelations]
  );

  const clientRefsOptions = useMemo(
    () =>
      (apiFilters.client_refs || []).map((r: any) => ({
        value: r.id,
        label: r.name
      })),
    [apiFilters.client_refs]
  );

  const clientCustomRefsOptions = useMemo(
    () =>
      (apiFilters.client_custom_refs || []).map((r: any) => ({
        value: r.id,
        label: r.name
      })),
    [apiFilters.client_custom_refs]
  );

  const priceBandOptions = useMemo(
    () =>
      (apiFilters.store_price_bands || []).map((p: any) => ({
        value: p.id,
        label: p.name
      })),
    [apiFilters.store_price_bands]
  );

  const cuisineOptions = useMemo(
    () =>
      isSpirits && !isOnTrade
        ? OFF_TRADE_SPIRIT_POS_TYPES
        : (apiFilters.store_primary_types || [])
            .filter(
              (t: any) =>
                !isSpirits ||
                !SPIRITS_EXCLUDED_POS_TYPES.includes(
                  String(t.name).trim().toLowerCase()
                )
            )
            .map((t: any) => ({
              value: t.id,
              label: t.name
            })),
    [apiFilters.store_primary_types, isSpirits, isOnTrade]
  );

  const wsAwardOptions = useMemo(
    () =>
      (apiFilters.ws_award_options || []).map((o: any) => ({
        value: o.value,
        label: o.label
      })),
    [apiFilters.ws_award_options]
  );

  const michelinOptions = useMemo(
    () =>
      (apiFilters.michelin_star_options || []).map((o: any) => ({
        value: o.value,
        label: o.label
      })),
    [apiFilters.michelin_star_options]
  );

  const geoAreaOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', label: 'World' },
      ...continents.map((c) => ({ value: String(c.id), label: c.name }))
    ],
    [continents]
  );

  const countryOptions: SelectOption[] = useMemo(() => {
    const filtered =
      draft.continentId === 'all'
        ? countries
        : countries.filter(
            (c) => String(c.continent_id) === String(draft.continentId)
          );
    return [
      { value: 'all', label: 'All' },
      ...filtered
        .map((c) => ({ value: String(c.id), label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];
  }, [countries, draft.continentId]);

  const vintageOptions: SelectOption[] = useMemo(
    () =>
      (apiFilters.vintage_options || []).map((v: any) => ({
        value: v.value,
        label: v.label
      })),
    [apiFilters.vintage_options]
  );

  const posStatusOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: 'pos', label: 'Active' },
      { value: 'conquer', label: 'To Win Over' },
      { value: 'lost', label: 'Lost' }
    ],
    []
  );

  // All awards are shown in one multi-select "Awards" field, grouped by type
  // (non-selectable headers). Values are prefixed with their kind so they can
  // be routed back to their own PosFilterState field.
  const awardsOptions: SelectOption[] = useMemo(() => {
    const grouped = (
      kind: AwardKind,
      options: SelectOption[]
    ): SelectOption[] =>
      options.map((o) => ({
        value: `${kind}:${o.value}`,
        label: o.label,
        group: AWARD_GROUPS[kind]
      }));
    return isSpirits
      ? [
          ...grouped('wb', WORLDS_BEST_OPTIONS),
          ...grouped('bar', TOP_BAR_OPTIONS),
          ...grouped('sa', SPIRITED_AWARD_OPTIONS)
        ]
      : [
          ...grouped('ws', wsAwardOptions),
          ...grouped('michelin', michelinOptions)
        ];
  }, [isSpirits, wsAwardOptions, michelinOptions]);

  const selectedAwards: SelectOption[] = useMemo(() => {
    const optionByValue = new Map(
      awardsOptions.map((o) => [String(o.value), o])
    );
    const toSelected = (kind: AwardKind, o: SelectOption): SelectOption => {
      const value = `${kind}:${o.value}`;
      const known = optionByValue.get(value);
      const label = known?.label ?? o.label;
      return { value, label: `${AWARD_GROUPS[kind]} – ${label}` };
    };
    return [
      ...draft.storeWsAward.map((o) => toSelected('ws', o)),
      ...draft.storeMichelinStar.map((o) => toSelected('michelin', o)),
      ...draft.storeWorldsBest.map((o) => toSelected('wb', o)),
      ...draft.storeTopBar.map((o) => toSelected('bar', o)),
      ...draft.storeSpiritedAward.map((o) => toSelected('sa', o))
    ];
  }, [
    awardsOptions,
    draft.storeWsAward,
    draft.storeMichelinStar,
    draft.storeWorldsBest,
    draft.storeTopBar,
    draft.storeSpiritedAward
  ]);

  const handleAwardsChange = useCallback((val: SelectOption[]) => {
    const byKind: Record<AwardKind, SelectOption[]> = {
      ws: [],
      michelin: [],
      wb: [],
      bar: [],
      sa: []
    };
    val.forEach((opt) => {
      const [kind, ...rest] = String(opt.value).split(':');
      if (!(kind in byKind)) return;
      byKind[kind as AwardKind].push({
        value: rest.join(':'),
        label: opt.label.replace(/^[^–]+ – /, '')
      });
    });
    setDraft((prev) => ({
      ...prev,
      storeWsAward: byKind.ws,
      storeMichelinStar: byKind.michelin,
      storeWorldsBest: byKind.wb,
      storeTopBar: byKind.bar,
      storeSpiritedAward: byKind.sa
    }));
  }, []);

  const activeBracket = useMemo(() => {
    const [min, max] = draft.rangePrices;
    if (min === PRICE_RANGE_BOUNDS[0] && max === PRICE_RANGE_BOUNDS[1])
      return 'all';
    const match = PRICE_BRACKETS.find((b) => b.min === min && b.max === max);
    return match ? match.key : null;
  }, [draft.rangePrices]);

  const setField = useCallback(
    <K extends keyof PosFilterState>(key: K, value: PosFilterState[K]) => {
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const activeFilterCount = useMemo(
    () => countActiveFilters(draft, isOnTrade, isSpirits),
    [draft, isOnTrade, isSpirits]
  );

  if (!open) return null;

  return (
    <Overlay onClick={handleCancel}>
      <Panel
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <PanelHead>
          <HeadLeft>
            <FilterIconSvg>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
            </FilterIconSvg>
            <PanelTitle>{title}</PanelTitle>
            {activeFilterCount > 0 && (
              <CountBadge>{activeFilterCount}</CountBadge>
            )}
          </HeadLeft>

          <HeadActions>
            <ResetBtn type="button" onClick={handleReset}>
              Reset all
            </ResetBtn>
            <CloseBtn
              type="button"
              onClick={handleCancel}
              aria-label="Close filters"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </CloseBtn>
          </HeadActions>
        </PanelHead>

        {/* Body */}
        <PanelBody>
          {/* Market: where the sale happens */}
          <Segment>
            <SegmentHead>
              <SegmentTitle>Market</SegmentTitle>
            </SegmentHead>
            <FilterGrid>
              <FilterField>
                <FieldLabel>Geographical area</FieldLabel>
                <SelectDropdown
                  options={geoAreaOptions}
                  value={draft.continentId}
                  onChange={(val) => {
                    setDraft((prev) => ({
                      ...prev,
                      continentId: val,
                      countryId: 'all'
                    }));
                  }}
                />
              </FilterField>
              <FilterField>
                <FieldLabel>Country</FieldLabel>
                <SelectDropdown
                  options={countryOptions}
                  value={draft.countryId}
                  onChange={(val) => setField('countryId', val)}
                />
              </FilterField>
              <FilterField>
                <FieldLabel>Markets</FieldLabel>
                <MultiSelectDropdown
                  options={marketsOptions}
                  selected={draft.markets as SelectOption[]}
                  onChange={(val) =>
                    setDraft((prev) => ({ ...prev, markets: val }))
                  }
                />
              </FilterField>
            </FilterGrid>
          </Segment>

          {/* Product: what is being sold */}
          <Segment>
            <SegmentHead>
              <SegmentTitle>Product</SegmentTitle>
              <SegmentTag>{isSpirits ? 'Spirits' : 'Wine'}</SegmentTag>
            </SegmentHead>
            <FilterGrid>
              {!isSpirits && (
                <>
                  <FilterField>
                    <FieldLabel>Color</FieldLabel>
                    <SelectDropdown
                      options={colorsOptions}
                      value={draft.wineColor}
                      onChange={(val) => setField('wineColor', val)}
                    />
                  </FilterField>
                  <FilterField>
                    <FieldLabel>Grape</FieldLabel>
                    <MultiSelectDropdown
                      options={grapeOptions}
                      selected={draft.wineGrapeVariety as SelectOption[]}
                      onChange={(val) =>
                        setDraft((prev) => ({ ...prev, wineGrapeVariety: val }))
                      }
                    />
                  </FilterField>
                  <FilterField>
                    <FieldLabel>Wine Country</FieldLabel>
                    <SelectDropdown
                      options={wineCountryOptions}
                      value={draft.wineCountry}
                      onChange={(val) => {
                        setDraft((prev) => ({
                          ...prev,
                          wineCountry: val,
                          wineRegion: 'all'
                        }));
                      }}
                    />
                  </FilterField>
                  <FilterField>
                    <FieldLabel>Wine Region</FieldLabel>
                    <SelectDropdown
                      options={wineRegionOptions}
                      value={draft.wineRegion}
                      onChange={(val) => setField('wineRegion', val)}
                    />
                  </FilterField>
                  <FilterField>
                    <FieldLabel>Appellation</FieldLabel>
                    <SelectDropdown
                      options={appellationOptions}
                      value={draft.wineAppelation}
                      onChange={(val) => setField('wineAppelation', val)}
                    />
                  </FilterField>
                </>
              )}
              {isSpirits && (
                <>
                  <FilterField>
                    <FieldLabel>
                      {I18n.t('v2PosFilters.spiritCategory')}
                    </FieldLabel>
                    <SelectDropdown
                      options={spiritCategoryOptions}
                      value={draft.spiritCategory}
                      onChange={(val) => setField('spiritCategory', val)}
                    />
                  </FilterField>
                  <FilterField>
                    <FieldLabel>
                      {I18n.t('v2PosFilters.spiritCountry')}
                    </FieldLabel>
                    <SelectDropdown
                      options={spiritCountryOptions}
                      value={draft.spiritCountry}
                      onChange={(val) => setField('spiritCountry', val)}
                    />
                  </FilterField>
                </>
              )}
              <FilterField>
                <FieldLabel>Format</FieldLabel>
                {isSpirits && !isOnTrade ? (
                  <MultiSelectDropdown
                    options={OFF_TRADE_SPIRIT_FORMATS as SelectOption[]}
                    selected={draft.spiritFormatSize as SelectOption[]}
                    onChange={(val) => setField('spiritFormatSize', val)}
                  />
                ) : (
                  <SelectDropdown
                    options={isSpirits ? spiritFormatOptions : formatOptions}
                    value={draft.wineFormatGroup}
                    onChange={(val) => setField('wineFormatGroup', val)}
                  />
                )}
              </FilterField>
              <FilterField>
                <FieldLabel>Vintage</FieldLabel>
                <MultiSelectDropdown
                  options={vintageOptions}
                  selected={draft.vintage as SelectOption[]}
                  onChange={(val) =>
                    setDraft((prev) => ({ ...prev, vintage: val }))
                  }
                />
              </FilterField>
              <FilterField>
                <FieldLabel>Client Refs</FieldLabel>
                {clientCustomRefsOptions.length > 0 ? (
                  <MultiSelectDropdown
                    options={clientCustomRefsOptions}
                    selected={draft.clientCustomRefs as SelectOption[]}
                    onChange={(val) =>
                      setDraft((prev) => ({ ...prev, clientCustomRefs: val }))
                    }
                  />
                ) : (
                  <MultiSelectDropdown
                    options={clientRefsOptions}
                    selected={draft.clientRefs as SelectOption[]}
                    onChange={(val) =>
                      setDraft((prev) => ({ ...prev, clientRefs: val }))
                    }
                  />
                )}
              </FilterField>
            </FilterGrid>

            <PriceBrackets>
              <PriceBracketsHead>
                <PriceBracketsTitle>
                  {I18n.t('v2PosFilters.medianPrice', { currency })}
                </PriceBracketsTitle>
              </PriceBracketsHead>
              <BracketRow role="radiogroup" aria-label="Median price bracket">
                <BracketBtn
                  type="button"
                  $active={activeBracket === 'all'}
                  onClick={() => setField('rangePrices', PRICE_RANGE_BOUNDS)}
                >
                  {I18n.t('placeholderAll')}
                </BracketBtn>
                {PRICE_BRACKETS.map((b) => (
                  <BracketBtn
                    key={b.key}
                    type="button"
                    $active={activeBracket === b.key}
                    onClick={() => setField('rangePrices', [b.min, b.max])}
                  >
                    {b.min === 0
                      ? I18n.t('v2PosFilters.atMost', {
                          price: priceFormatter.format(b.max)
                        })
                      : I18n.t('v2PosFilters.priceRange', {
                          min: priceFormatter.format(b.min),
                          max: priceFormatter.format(b.max)
                        })}
                  </BracketBtn>
                ))}
              </BracketRow>
            </PriceBrackets>
          </Segment>

          {/* Account: where & how the product is listed */}
          <Segment>
            <SegmentHead>
              <SegmentTitle>Account</SegmentTitle>
            </SegmentHead>
            <FilterGrid>
              {(isOnTrade || isSpirits) && (
                <FilterField>
                  <FieldLabel>POS Type / Cuisine</FieldLabel>
                  <MultiSelectDropdown
                    options={cuisineOptions}
                    searchable
                    selected={draft.storePrimaryType as SelectOption[]}
                    onChange={(val) =>
                      setDraft((prev) => ({ ...prev, storePrimaryType: val }))
                    }
                  />
                </FilterField>
              )}
              {isOnTrade && (
                <>
                  <FilterField>
                    <FieldLabel>Menu Price Level</FieldLabel>
                    <MultiSelectDropdown
                      options={priceBandOptions}
                      selected={draft.storePriceBand as SelectOption[]}
                      onChange={(val) =>
                        setDraft((prev) => ({ ...prev, storePriceBand: val }))
                      }
                    />
                  </FilterField>
                  <FilterField>
                    <FieldLabel>Awards</FieldLabel>
                    <MultiSelectDropdown
                      options={awardsOptions}
                      searchable
                      selected={selectedAwards}
                      onChange={handleAwardsChange}
                    />
                  </FilterField>
                </>
              )}
              <FilterField>
                <FieldLabel>POS Status</FieldLabel>
                <SelectDropdown
                  options={posStatusOptions}
                  value={draft.posStatus}
                  onChange={(val) => setField('posStatus', val)}
                />
              </FilterField>
            </FilterGrid>
          </Segment>
        </PanelBody>

        <PanelFoot>
          <CancelBtn type="button" onClick={handleCancel}>
            Cancel
          </CancelBtn>
          <ApplyBtn type="button" onClick={handleApply}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Apply filters
          </ApplyBtn>
        </PanelFoot>
      </Panel>
    </Overlay>
  );
};

// --- Styled ---

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(-16px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(43, 61, 84, 0.42);
  backdrop-filter: blur(2px);
  z-index: 5000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 60px 24px 24px;
  overflow-y: auto;
  animation: ${fadeIn} 0.15s ease-out;
`;

const Panel = styled.div`
  width: min(1180px, 100%);
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 14px;
  overflow: hidden;
  box-shadow:
    0 24px 64px rgba(43, 61, 84, 0.22),
    0 4px 12px rgba(43, 61, 84, 0.08);
  animation: ${popIn} 0.18s cubic-bezier(0.16, 1, 0.3, 1);
`;

const PanelHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 22px;
  border-bottom: 1px solid var(--line-soft);
  background: var(--paper);
  gap: 14px;
  flex-wrap: wrap;
`;

const HeadLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FilterIconSvg = styled.span`
  svg {
    width: 15px;
    height: 15px;
    color: var(--terracotta);
    stroke-width: 2;
  }
`;

const PanelTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.005em;
  white-space: nowrap;
`;

const CountBadge = styled.span`
  background: var(--ink);
  color: var(--white);
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  margin-left: 2px;
`;

const HeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

const ResetBtn = styled.button`
  background: transparent;
  border: 1px solid var(--line);
  border-radius: 7px;
  color: var(--ink-muted);
  font-size: 11.5px;
  font-weight: 500;
  padding: 6px 13px;
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s,
    background 0.15s;

  &:hover {
    border-color: var(--terracotta);
    color: var(--terracotta);
    background: rgba(200, 75, 31, 0.04);
  }
`;

const CloseBtn = styled.button`
  background: transparent;
  border: 1px solid transparent;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 7px;
  cursor: pointer;
  color: var(--ink-muted);
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: var(--white);
    border-color: var(--line);
    color: var(--ink);
  }
`;

const PanelBody = styled.div`
  padding: 22px 24px 18px;
  overflow-y: auto;
  flex: 1;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px 30px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FilterField = styled.div``;

const FieldLabel = styled.label`
  display: block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-muted);
  margin-bottom: 6px;
`;

const Segment = styled.div`
  & + & {
    margin-top: 22px;
    padding-top: 20px;
    border-top: 1px solid var(--line-soft);
  }
`;

const SegmentHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 14px;
`;

const SegmentTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
`;

const SegmentTag = styled.span`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--ink-faint);
  background: var(--paper);
  border: 1px solid var(--line-soft);
  border-radius: 999px;
  padding: 2px 9px;
`;

const PriceBrackets = styled.div`
  margin-top: 22px;
`;

const PriceBracketsHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 10px;
`;

const PriceBracketsTitle = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-muted);
`;

const BracketRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const BracketBtn = styled.button<{ $active: boolean }>`
  border: 1px solid var(--line);
  background: var(--white);
  color: var(--ink);
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: var(--font-sans);
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;

  &:hover {
    border-color: var(--terracotta);
    color: var(--terracotta);
  }

  ${({ $active }) =>
    $active &&
    css`
      background: var(--terracotta);
      border-color: var(--terracotta);
      color: var(--white);

      &:hover {
        color: var(--white);
      }
    `}
`;

const PanelFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 16px 28px;
  border-top: 1px solid var(--line);
  flex-shrink: 0;
`;

const CancelBtn = styled.button`
  border: 1px solid var(--line);
  background: var(--white);
  color: var(--ink);
  padding: 10px 22px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: var(--font-sans);
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: var(--paper);
    border-color: var(--ink-faint);
  }
`;

const ApplyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--terracotta);
  color: var(--white);
  border: none;
  padding: 10px 22px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: var(--font-sans);
  transition: background 0.15s;

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover {
    background: var(--terracotta-dark, #a63d18);
  }
`;
